import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Clock,
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  User,
  Share2,
  ShoppingCart,
  BadgeCheck,
  Star,
  X,
} from "lucide-react";
import { SectionEyebrow } from "@/components/ui-bits";
import {
  BLOG_ARTICLES,
  BLOG_CATEGORIES,
  getArticleBySlug,
  getRelatedArticles,
} from "@/data/blogArticles";

const SITE_URL = "https://ogonotary.com";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const canonical = `${SITE_URL}/blog/${article.slug}`;
  const cat = BLOG_CATEGORIES[article.category];
  const related = getRelatedArticles(article.slug, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${canonical}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: article.title,
    name: article.title,
    description: article.description,
    url: canonical,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    inLanguage: "en-US",
    keywords: article.keywords,
    articleSection: cat?.label || "Blog",
    wordCount: article.body
      .filter((b) => b.type === "p" || b.type === "callout" || b.type === "quote")
      .reduce((n, b) => n + (b.text || "").split(/\s+/).length, 0),
    author: {
      "@type": "Person",
      name: article.author,
      url: `${SITE_URL}/about`,
      jobTitle:
        "Founder, Florida Commissioned Notary Public & Ordained Minister",
    },
    publisher: {
      "@type": "Organization",
      name: "OGO Notary & Signing Services",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/img/ogo-logo.png`,
      },
    },
    image: article.heroImage
      ? [article.heroImage]
      : [`${SITE_URL}/img/ogo-logo.png`],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  };

  const seoTitle = article.seoTitle || `${article.title} | OGO Notary`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={article.description} />
        <meta name="keywords" content={article.keywords} />
        <meta name="author" content={article.author} />
        <link rel="canonical" href={canonical} />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta
          property="article:modified_time"
          content={article.updatedAt || article.publishedAt}
        />
        <meta property="article:author" content={article.author} />
        {cat && <meta property="article:section" content={cat.label} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.description} />
        <meta property="og:url" content={canonical} />
        {article.heroImage && (
          <meta property="og:image" content={article.heroImage} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.description} />
        {article.heroImage && (
          <meta name="twitter:image" content={article.heroImage} />
        )}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-white/10"
        data-testid={`blog-article-${article.slug}`}
      >
        {article.heroImage && (
          <>
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-cover pointer-events-none"
              style={{
                backgroundImage: `url(${article.heroImage})`,
                backgroundPosition: "center",
                opacity: 0.22,
              }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-[#142339]/70 via-[#142339]/60 to-[#142339] pointer-events-none"
            />
          </>
        )}
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 py-20 sm:py-28">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-300 hover:text-[#D4AF37] transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All Articles
          </Link>
          {cat && (
            <SectionEyebrow className="mb-5">{cat.label}</SectionEyebrow>
          )}
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            {article.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.18em] text-slate-300">
            <span className="inline-flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-[#D4AF37]" /> {article.author}
            </span>
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-[#D4AF37]" />
              {formatDate(article.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-[#D4AF37]" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-lg sm:text-xl text-slate-200 leading-relaxed font-serif-display italic border-l-2 border-[#D4AF37] pl-6 mb-12">
            {article.excerpt}
          </p>
          <ArticleBody blocks={article.body} />

          <ShareBar article={article} canonical={canonical} />
          <AuthorCard author={article.author} />
        </div>
      </article>

      {/* Floating "Shop the Complete Kit" pill — only when article has an ideaListUrl */}
      {article.ideaListUrl && (
        <FloatingIdeaListPill href={article.ideaListUrl} />
      )}

      {/* Related */}
      {related.length > 0 && (
        <section
          className="py-20 sm:py-24 border-b border-white/10"
          data-testid="blog-related"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <SectionEyebrow className="mb-8">Keep Reading</SectionEyebrow>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="group border border-white/10 bg-[#1A2B45] p-6 hover:border-[#D4AF37]/60 transition-colors flex flex-col"
                >
                  {BLOG_CATEGORIES[r.category] && (
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
                      {BLOG_CATEGORIES[r.category].label}
                    </div>
                  )}
                  <h4 className="font-serif-display text-xl text-white leading-tight mb-3 group-hover:text-[#D4AF37] transition-colors">
                    {r.title}
                  </h4>
                  <p className="text-slate-400 text-sm line-clamp-2 mb-5">
                    {r.excerpt}
                  </p>
                  <div className="mt-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                    Read <ArrowUpRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function ArticleBody({ blocks }) {
  return (
    <div className="space-y-6" data-testid="blog-body">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "h2":
            return (
              <h2
                key={i}
                className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mt-10 mb-2 pt-4 border-t border-white/10"
              >
                {block.text}
              </h2>
            );
          case "h3":
            return (
              <h3
                key={i}
                className="font-serif-display text-xl sm:text-2xl text-[#D4AF37] leading-tight mt-6"
              >
                {block.text}
              </h3>
            );
          case "p":
            return (
              <p
                key={i}
                className="text-slate-200 text-base sm:text-lg leading-[1.85]"
              >
                {block.text}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="space-y-3 my-4 pl-1">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-2.5 w-1.5 h-1.5 shrink-0 bg-[#D4AF37]"
                    />
                    <span className="text-slate-200 text-base sm:text-lg leading-[1.75]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="space-y-3 my-4 pl-1 counter-reset-blog">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="shrink-0 font-serif-display text-[#D4AF37] text-lg leading-none w-6 text-right"
                    >
                      {j + 1}.
                    </span>
                    <span className="text-slate-200 text-base sm:text-lg leading-[1.75]">
                      {item}
                    </span>
                  </li>
                ))}
              </ol>
            );
          case "callout":
            return (
              <aside
                key={i}
                className="my-8 border-l-2 border-[#D4AF37] bg-[#D4AF37]/5 p-6"
              >
                {block.title && (
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">
                    {block.title}
                  </div>
                )}
                <p className="text-slate-100 text-base sm:text-lg leading-relaxed">
                  {block.text}
                </p>
              </aside>
            );
          case "quote":
            return (
              <blockquote
                key={i}
                className="my-8 border-l-4 border-[#D4AF37] pl-6 italic font-serif-display"
              >
                <p className="text-slate-100 text-xl sm:text-2xl leading-relaxed">
                  {block.text}
                </p>
                {block.cite && (
                  <cite className="mt-3 block not-italic text-[10px] uppercase tracking-[0.22em] text-slate-400">
                    — {block.cite}
                  </cite>
                )}
              </blockquote>
            );
          case "badge": {
            const tested = block.kind === "tested";
            const Icon = tested ? BadgeCheck : Star;
            return (
              <div
                key={i}
                className="my-4 flex flex-wrap items-center gap-2"
              >
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] font-bold border ${
                    tested
                      ? "bg-[#D4AF37] text-[#142339] border-[#D4AF37]"
                      : "bg-transparent text-[#D4AF37] border-[#D4AF37]/70"
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {tested ? "OGO TESTED" : "OGO PICK"}
                </span>
                {block.detail && (
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-300">
                    — {block.detail}
                  </span>
                )}
              </div>
            );
          }
          case "cta": {
            const prominent = block.variant === "prominent";
            return (
              <div key={i} className="my-6 flex flex-col items-start gap-2">
                <a
                  href={block.href}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  data-testid={block.testid || "blog-amazon-cta"}
                  className={
                    prominent
                      ? "w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-8 py-5 text-sm uppercase tracking-[0.22em] font-bold transition-colors"
                      : "inline-flex items-center gap-2.5 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
                  }
                >
                  <ShoppingCart className={prominent ? "w-4 h-4" : "w-3.5 h-3.5"} />
                  {block.label}
                  <ArrowUpRight className={prominent ? "w-4 h-4" : "w-3.5 h-3.5"} />
                </a>
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {block.disclosure || "#CommissionsEarned"} · As an Amazon Associate we earn from qualifying purchases.
                </span>
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

function ShareBar({ article, canonical }) {
  const shareUrl = encodeURIComponent(canonical);
  const shareTitle = encodeURIComponent(article.title);
  return (
    <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400">
        <Share2 className="w-3.5 h-3.5 text-[#D4AF37]" /> Share this article
      </div>
      <div className="flex flex-wrap gap-2">
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="blog-share-linkedin"
          className="text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-300 px-3 py-2 transition-colors"
        >
          LinkedIn
        </a>
        <a
          href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="blog-share-x"
          className="text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-300 px-3 py-2 transition-colors"
        >
          X / Twitter
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="blog-share-facebook"
          className="text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-300 px-3 py-2 transition-colors"
        >
          Facebook
        </a>
        <a
          href={`mailto:?subject=${shareTitle}&body=${shareUrl}`}
          data-testid="blog-share-email"
          className="text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-300 px-3 py-2 transition-colors"
        >
          Email
        </a>
      </div>
    </div>
  );
}

function AuthorCard({ author }) {
  return (
    <div className="mt-10 border border-white/10 bg-[#1A2B45] p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6">
      <img
        src="/img/headshot-greg.jpg"
        alt={author}
        className="w-20 h-20 rounded-full border-2 border-[#D4AF37] object-cover object-top shrink-0"
      />
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">
          Written by
        </div>
        <h4 className="font-serif-display text-2xl text-white mb-2">
          {author}
        </h4>
        <p className="text-slate-300 text-sm leading-relaxed">
          Founder, OGO Notary & Signing Services. 45 years of federal-grade
          document and operational discipline — Department of Defense, DLA, U.S.
          State Department. Florida Commissioned Notary Public, NNA-certified
          Signing Agent, and Ordained Minister. Built on Experience. Grounded
          in Faith.
        </p>
        <Link
          to="/about"
          className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#FDE047] font-bold"
        >
          More about Dr. Byers <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

// Keep the article index available for sitemap and internal reference.
export { BLOG_ARTICLES };

function FloatingIdeaListPill({ href }) {
  const [visible, setVisible] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    function onScroll() {
      // Appear once the user has scrolled past ~600px (roughly past the hero)
      // and hide once they hit the last 800px of the page (near the Idea List CTA).
      const scrollY = window.scrollY;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const nearBottom = maxScroll - scrollY < 800;
      setVisible(scrollY > 600 && !nearBottom);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (dismissed) return null;

  return (
    <div
      data-testid="floating-idea-list-pill"
      aria-hidden={!visible}
      className={`fixed z-40 bottom-5 right-5 sm:bottom-8 sm:right-8 flex items-stretch transition-all duration-300 ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        data-testid="floating-idea-list-cta"
        className="inline-flex items-center gap-2.5 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] pl-4 pr-5 py-3.5 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-bold shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-colors"
      >
        <ShoppingCart className="w-4 h-4" />
        <span className="hidden sm:inline">Shop the Complete Kit</span>
        <span className="sm:hidden">Shop Kit</span>
        <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss shop pill"
        data-testid="floating-idea-list-dismiss"
        className="inline-flex items-center justify-center bg-[#0B1120] hover:bg-[#142339] border-l border-[#0B1120] text-slate-300 hover:text-white w-9 shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
